import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { LANGS, setDictEntry, lookup } from "@/lib/i18n";
import { UI_KEYS } from "@/lib/i18nKeys";
import { extractParen } from "@/lib/format";
import { CGV_TITLE, CGV_FOOTER, CGV_SECTIONS } from "@/lib/cgv";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Wand2, Save, CheckCheck } from "lucide-react";
import { GuidedReviewDialog, findSimilar } from "@/components/GuidedReviewDialog";

const Row = ({ k, value, onSave, isAuto }) => {
  const [v, setV] = useState(value || "");
  useEffect(() => { setV(value || ""); }, [value]);
  return (
    <div className="grid md:grid-cols-[1fr_1fr_auto] gap-2 py-2 border-b border-border items-start" data-testid="tr-row">
      <div className="text-xs whitespace-pre-wrap text-muted-foreground">{k}{isAuto && <span className="ml-2 inline-block rounded bg-amber-100 text-amber-800 px-1.5 text-[10px] font-semibold" data-testid="tr-auto-badge">Auto · à relire</span>}</div>
      <Textarea rows={k.length > 90 ? 4 : 1} value={v} onChange={(e) => setV(e.target.value)} className={`text-xs ${value ? "" : "border-destructive/60"}`} data-testid="tr-input" />
      <div className="flex">
        <Button size="icon" variant="ghost" disabled={v === (value || "")} onClick={() => onSave(k, v)} data-testid="tr-save"><Save className="w-4 h-4" /></Button>
        {isAuto && <Button size="icon" variant="ghost" title="Valider cette traduction telle quelle" disabled={!value || v !== value} onClick={() => onSave(k, v)} data-testid="tr-validate"><CheckCheck className="w-4 h-4 text-emerald-600" /></Button>}
      </div>
    </div>
  );
};

export default function Translations() {
  const { refresh } = useI18n();
  const { user } = useAuth();
  const fixedLang = user?.role === "admin" ? "" : (user?.translate_lang || "");
  const [lang, setLang] = useState(fixedLang && fixedLang !== "fr" ? fixedLang : "en");
  const [dict, setDict] = useState({});
  const [catalogKeys, setCatalogKeys] = useState([]);
  const [q, setQ] = useState("");
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [onlyAuto, setOnlyAuto] = useState(false);
  const [autoKeys, setAutoKeys] = useState(new Set());
  const [proposal, setProposal] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all(["van", "prosport"].map((r) => Promise.all([api.get(`/catalog/${r}`).then((x) => x.data).catch(() => ({ models: [], options: [] })), api.get(`/menu/${r}`).then((x) => x.data.groups || []).catch(() => [])])))
      .then((cats) => setCatalogKeys(cats.flatMap(([c, groups]) => [...c.models.map((m) => m.name.replace(/\s*\(.*\)\s*$/, "").trim()), ...c.models.map((m) => m.details || extractParen(m.name)), ...c.options.map((o) => o.name), ...c.options.map((o) => o.details), ...c.options.map((o) => extractParen(o.name)), ...c.options.map((o) => o.category).filter(Boolean), ...groups.map((g) => g.label.replace(/diagonal\b/gi, "diagonale"))])));
  }, []);
  const loadAuto = () => api.get(`/i18n/${lang}/auto-keys`).then(({ data }) => setAutoKeys(new Set(data))).catch(() => setAutoKeys(new Set()));
  useEffect(() => { api.get(`/i18n/${lang}`).then(({ data }) => setDict(data)); loadAuto(); }, [lang]); // eslint-disable-line react-hooks/exhaustive-deps

  const keys = useMemo(() => {
    const byLower = new Map(Object.keys(dict).map((k) => [k.toLowerCase(), k]));
    const all = [...UI_KEYS, ...catalogKeys, CGV_TITLE, CGV_FOOTER, ...CGV_SECTIONS.flat(), ...Object.keys(dict)].filter(Boolean);
    return [...new Set(all.map((k) => byLower.get(k.toLowerCase()) || k))].filter((k, i, a) => a.findIndex((x) => x.toLowerCase() === k.toLowerCase()) === i);
  }, [catalogKeys, dict]);
  const shown = keys.filter((k) => (!onlyMissing || !lookup(dict, k)) && (!onlyAuto || autoKeys.has(k)) && (!q || (k + " " + (dict[k] || "")).toLowerCase().includes(q.toLowerCase())));
  const missing = keys.filter((k) => !lookup(dict, k)).length;
  const autoCount = keys.filter((k) => autoKeys.has(k)).length;

  const store = async (k, v) => {
    await api.put("/i18n", { lang, key: k, text: v });
    setDict((d) => ({ ...d, [k]: v })); setDictEntry(lang, k, v);
    setAutoKeys((s) => { const n = new Set(s); n.delete(k); return n; });
  };
  const save = async (k, v) => {
    const old = (dict[k] || "").trim();
    try {
      await store(k, v);
      refresh();
      toast.success("Traduction enregistrée");
      const to = v.trim();
      const rows = old && to && old.toLowerCase() !== to.toLowerCase() ? findSimilar(dict, k, old) : [];
      if (rows.length) setProposal({ from: old, to, rows });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const applyProposal = async (items) => {
    let ok = 0;
    for (const it of items) {
      try { await store(it.key, it.text); ok += 1; } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); break; }
    }
    refresh(); setProposal(null);
    if (ok) toast.success(`${ok} ligne(s) corrigée(s)`);
  };
  const auto = async () => {
    setBusy(true);
    try {
      for (let i = 0; i < 30; i += 1) {
        const { data } = await api.post("/i18n/auto", { lang, keys });
        if (!data.remaining || !data.translated) break;
      }
      const { data } = await api.get(`/i18n/${lang}`); setDict(data); loadAuto();
      toast.success("Traductions automatiques ajoutées");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-4" data-testid="translations-page">
      <h1 className="font-display text-2xl font-bold">Traductions</h1>
      <div className="flex flex-wrap items-center gap-2">
        <select value={lang} onChange={(e) => setLang(e.target.value)} className="h-9 rounded-md border border-input bg-background px-2 text-sm" data-testid="tr-lang">
          {LANGS.filter(([c]) => c !== "fr" && (!fixedLang || c === fixedLang)).map(([c, n]) => <option key={c} value={c}>{n}</option>)}
        </select>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…" className="w-64 h-9" data-testid="tr-search" />
        <label className="text-sm flex items-center gap-1.5"><input type="checkbox" checked={onlyMissing} onChange={(e) => setOnlyMissing(e.target.checked)} data-testid="tr-missing" />Manquantes uniquement</label>
        <label className="text-sm flex items-center gap-1.5"><input type="checkbox" checked={onlyAuto} onChange={(e) => setOnlyAuto(e.target.checked)} data-testid="tr-only-auto" />Traductions automatiques <span className="text-xs text-muted-foreground" data-testid="tr-auto-count">({autoCount} à relire)</span></label>
        <span className="text-xs text-muted-foreground" data-testid="tr-count">{keys.length - missing}/{keys.length} traduits</span>
        <Button onClick={auto} disabled={busy || !missing} className="gap-2 ml-auto" data-testid="tr-auto"><Wand2 className="w-4 h-4" />{busy ? "Traduction…" : "Traduire automatiquement les manquants"}</Button>
      </div>
      <div className="text-xs text-muted-foreground grid md:grid-cols-[1fr_1fr_auto] gap-2 font-semibold"><span>Français</span><span>Traduction</span><span /></div>
      {shown.slice(0, 400).map((k) => <Row key={k} k={k} value={dict[k]} onSave={save} isAuto={autoKeys.has(k)} />)}
      <GuidedReviewDialog proposal={proposal} onClose={() => setProposal(null)} onApply={applyProposal} />
    </div>
  );
}
